import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Bug, Home, ExternalLink, Undo2 } from 'lucide-react';

export interface BaseErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  errorType?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showRetry?: boolean;
  showHomeButton?: boolean;
  showErrorDetails?: boolean;
  className?: string;
  // Specialized props for different error boundary types
  onRetry?: () => void;
  onReset?: () => void;
  onRefresh?: () => void;
  showResetButton?: boolean;
  showSaveButton?: boolean;
  showRefreshButton?: boolean;
  showDownloadButton?: boolean;
  dataType?: string;
  maxRetries?: number;
  autoRetry?: boolean;
}

export interface BaseErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
  timestamp: Date;
  retryCount: number;
  /** True while an automatic retry is scheduled (show fallback "Retrying..." UI). */
  isAutoRetrying?: boolean;
}

export abstract class BaseErrorBoundary<
  P extends BaseErrorBoundaryProps = BaseErrorBoundaryProps,
  S extends BaseErrorBoundaryState = BaseErrorBoundaryState
> extends Component<P, S> {
  protected maxRetries: number;
  protected retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: P) {
    super(props);
    this.maxRetries = props.maxRetries || 3;
    this.state = {
      hasError: false,
      errorId: this.generateErrorId(),
      timestamp: new Date(),
      retryCount: 0,
    } as S;
  }

  protected generateErrorId(): string {
    const prefix = this.props.errorType ? `${this.props.errorType.toLowerCase()}_error` : 'error';
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { 
      hasError: true, 
      error,
      timestamp: new Date()
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`${this.constructor.name} caught an error:`, error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    } as Partial<S>);

    // Log error to analytics/error reporting service
    this.logError(error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Handle auto-retry for specific error types
    if (this.props.autoRetry && this.shouldAutoRetry(error)) {
      this.handleAutoRetry();
    }
  }

  componentWillUnmount() {
    // Clear any pending timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  protected shouldAutoRetry(_error: Error): boolean {
    // Override in subclasses for specific auto-retry logic
    return false;
  }

  protected handleAutoRetry() {
    if (this.state.retryCount >= this.maxRetries) return;
    console.log(`🔄 Auto-retrying (attempt ${this.state.retryCount + 1}/${this.maxRetries})`);
    this.setState({ isAutoRetrying: true } as Partial<S>);
    this.scheduleRetry(2000);
  }

  protected scheduleRetry(delay: number) {
    const timeout = setTimeout(() => {
      this.setState({ isAutoRetrying: false } as Partial<S>);
      this.handleRetry();
    }, delay);
    this.retryTimeouts.push(timeout);
  }

  protected handleRetry = () => {
    console.log('🔄 Retrying...');
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      isAutoRetrying: false,
      retryCount: prevState.retryCount + 1,
    } as Partial<S>));
  };

  protected handleReset = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      isAutoRetrying: false,
      retryCount: 0,
    } as Partial<S>);
  };

  protected handleRefresh = () => {
    if (this.props.onRefresh) {
      this.props.onRefresh();
    }
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      isAutoRetrying: false,
      retryCount: 0,
    } as Partial<S>);
  };

  protected logError = (error: Error, errorInfo: ErrorInfo) => {
    const errorData = {
      errorId: this.state.errorId,
      componentName: this.props.componentName || 'Unknown Component',
      errorType: this.props.errorType || 'General Error',
      dataType: this.props.dataType,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
      timestamp: this.state.timestamp.toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      environment: process.env.NODE_ENV,
      retryCount: this.state.retryCount,
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${this.constructor.name} Error Report`);
      console.log('Error ID:', errorData.errorId);
      console.log('Component:', errorData.componentName);
      console.log('Error Type:', errorData.errorType);
      if (errorData.dataType) console.log('Data Type:', errorData.dataType);
      console.log('Error:', errorData.error);
      console.log('Component Stack:', errorData.errorInfo.componentStack);
      console.log('Timestamp:', errorData.timestamp);
      console.log('Retry Count:', errorData.retryCount);
      console.groupEnd();
    }

    // Send to error reporting service
    this.sendToErrorService(errorData);
  };

  protected sendToErrorService = (errorData: Record<string, unknown>) => {
    // Example integration with error reporting service
    // Replace this with your actual error reporting service
    try {
      // Sentry.captureException(errorData.error, { extra: errorData });
      // LogRocket.captureException(errorData.error, { extra: errorData });
      
      // For now, just log to console
      if (process.env.NODE_ENV === 'production') {
        console.error('Error should be sent to error reporting service:', errorData);
      }
    } catch (reportingError) {
      console.error('Failed to send error to reporting service:', reportingError);
    }
  };

  /** Compact fallback shown during automatic retry (e.g. chunk load). Override in subclasses. */
  protected renderRetryingUI(): ReactNode {
    return (
      <div
        className={`min-h-[200px] flex items-center justify-center p-6 ${this.props.className || ''}`}
        role="status"
        aria-live="polite"
        aria-label="Retrying"
      >
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <RefreshCw className="h-8 w-8 animate-spin" aria-hidden />
          <p className="text-sm font-medium">Retrying...</p>
          <p className="text-xs text-gray-500">Attempt {this.state.retryCount + 1} of {this.maxRetries}</p>
        </div>
      </div>
    );
  }

  protected renderErrorUI(): ReactNode {
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div 
        className={`min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6 ${this.props.className || ''}`}
        role="alert"
        aria-live="assertive"
        aria-labelledby="error-title"
        aria-describedby="error-description"
      >
        <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl p-8 text-center border border-red-100">
          <div 
            className="p-4 bg-red-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center"
            aria-hidden="true"
          >
            <AlertTriangle className="h-10 w-10 text-red-600" />
          </div>
          
          <h2 
            id="error-title"
            className="text-2xl font-bold text-gray-900 mb-3"
          >
            Something went wrong
          </h2>
          
          <p 
            id="error-description"
            className="text-gray-600 mb-6 leading-relaxed"
          >
            {this.props.componentName 
              ? `We encountered an error while loading the ${this.props.componentName}.`
              : "We encountered an unexpected error."
            } Please try again or contact support if the problem persists.
          </p>

          {this.state.errorId && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Error ID:</span> {this.state.errorId}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Please include this ID when contacting support.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {this.props.showRetry && (
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                aria-describedby="retry-description"
              >
                <RefreshCw className="h-5 w-5" />
                Try Again
              </button>
            )}

            {this.props.showResetButton && (
              <button
                onClick={this.handleReset}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <Undo2 className="h-5 w-5" />
                Reset Form
              </button>
            )}

            {this.props.showRefreshButton && (
              <button
                onClick={this.handleRefresh}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-5 w-5" />
                Refresh Data
              </button>
            )}

            {this.props.showHomeButton && (
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <Home className="h-5 w-5" />
                Go Home
              </button>
            )}
          </div>

          {this.props.showErrorDetails && process.env.NODE_ENV === 'development' && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 font-medium">
                <Bug className="inline h-4 w-4 mr-1" />
                Technical Details (Development)
              </summary>
              <div 
                className="mt-3 p-4 bg-gray-100 rounded-lg text-xs font-mono text-gray-800 overflow-auto max-h-64"
                role="group"
                aria-label="Technical error information"
              >
                <div className="mb-3">
                  <strong>Error:</strong> {this.state.error?.toString()}
                </div>
                
                {this.state.error?.stack && (
                  <div className="mb-3">
                    <strong>Stack Trace:</strong>
                    <pre className="mt-1 whitespace-pre-wrap text-xs">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
                
                {this.state.errorInfo && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 whitespace-pre-wrap text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <button
                    onClick={() => {
                      const errorText = `Error ID: ${this.state.errorId}\nError: ${this.state.error?.toString()}\nStack: ${this.state.error?.stack}\nComponent Stack: ${this.state.errorInfo?.componentStack}`;
                      navigator.clipboard.writeText(errorText);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Copy Error Details
                  </button>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.isAutoRetrying) {
      return this.renderRetryingUI();
    }
    if (this.state.hasError) {
      return this.renderErrorUI();
    }

    return this.props.children;
  }
}
